import { PRIMARY_USE_OPTIONS } from '@parcel/filters';
import { PROJECT_ACCOUNT_ROLE_DEFINITIONS } from '@parcel/types';
import type { ApiAccountPortfolio } from '../../lib/accounts.js';
import { formatSqft, stageMeta } from '../../lib/projects-display.js';
import {
  type DetailField,
  DetailFieldGrid,
  PropertySection,
} from '../entity-drawer/entity-drawer-sections.js';
import { Hint } from '../hint.js';

const USE_LABEL = new Map(PRIMARY_USE_OPTIONS.map((option) => [option.value, option.label]));
const ROLE_LABEL = new Map(PROJECT_ACCOUNT_ROLE_DEFINITIONS.map((d) => [d.value, d.label]));

const usdCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

function humanize(value: string): string {
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// A total known for under a quarter of the projects is flagged, so a small sum never reads as the whole.
function total(label: string, amount: string, known: number, projects: number): DetailField | null {
  if (known === 0) return null;
  return {
    label,
    value: (
      <>
        {amount}
        {known < projects / 4 ? (
          <Hint label={`Known for ${known} of ${projects} projects`}>
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              partial
            </span>
          </Hint>
        ) : null}
      </>
    ),
  };
}

function Mix({ label, entries }: { label: string; entries: Array<[string, number]> }) {
  if (entries.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {entries.map(([name, projects]) => (
          <span key={name} className="rounded-full border px-2 py-0.5 text-xs text-foreground">
            {name}
            <span className="ml-1 text-muted-foreground">{projects}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** The firm's portfolio, derived from its linked projects; nothing when it has none. */
export function AccountPortfolioSection({
  portfolio,
  columns = 2,
}: {
  portfolio: ApiAccountPortfolio;
  columns?: 1 | 2;
}) {
  const projects = portfolio.total_projects;
  if (projects === 0) return null;
  const totals = [
    { label: 'Projects', value: `${projects} · ${portfolio.active_projects} active` },
    total('Square feet', formatSqft(portfolio.known_sf), portfolio.known_sf_projects, projects),
    total(
      'Construction cost',
      usdCompact.format(portfolio.known_cost_usd),
      portfolio.known_cost_projects,
      projects,
    ),
    total(
      'Units',
      portfolio.known_units.toLocaleString('en-US'),
      portfolio.known_units_projects,
      projects,
    ),
  ].filter((field): field is DetailField => field !== null);
  return (
    <div data-testid="account-portfolio">
      <PropertySection title="Portfolio">
        <DetailFieldGrid fields={totals} columns={columns} />
        <div className="space-y-3 pt-1">
          <Mix
            label="Stage"
            entries={portfolio.stage_mix.map((s) => [stageMeta(s.stage).label, s.projects])}
          />
          <Mix
            label="Area of work"
            entries={portfolio.area_of_work.map((a) => [`${a.city}, ${a.state}`, a.projects])}
          />
          <Mix
            label="Sectors"
            entries={portfolio.sectors.map((s) => [
              s.primary_use ? (USE_LABEL.get(s.primary_use) ?? humanize(s.primary_use)) : 'Unknown',
              s.projects,
            ])}
          />
          <Mix
            label="Roles"
            entries={portfolio.roles_played.map((r) => [
              ROLE_LABEL.get(r.role) ?? humanize(r.role),
              r.projects,
            ])}
          />
        </div>
      </PropertySection>
    </div>
  );
}
